import { useCallback, useEffect, useMemo, useState } from 'react';
import { bulkReportService, type PipelineData } from '../services/bulkReportService';
import { buildBoardRows, type BoardRow } from '../lib/boardRows';
import type { BillVerdict } from '../lib/billVerdict';
import type { SendHistory } from '../lib/reportStatus';
import type { BvBill } from '../types/billTypes';

// The Bulk Bill Run board's rows by bill id, for the lists that show a bill's
// status without being the board (Bills, Bulk Customer Report): status, report
// bucket and send status come from one place, so the screens cannot disagree.
// Secondary: when the pipeline cannot load, those lists still list their bills
// with no status ('—'), as the Bills list always has.

export type BoardRowsState = {
  rowsById: Map<string, BoardRow>;
  /** Every bill's send history; null until loaded, or when it failed. */
  sendHistory: Record<string, SendHistory> | null;
  reload: () => void;
};

export function useBoardRows(verdictFor: (bill: BvBill) => BillVerdict | null): BoardRowsState {
  const [data, setData] = useState<PipelineData | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let mounted = true;
    bulkReportService.loadPipeline()
      .then((res) => {
        if (mounted && res.success && res.data) setData(res.data);
      })
      .catch((err: unknown) => console.error('Bill statuses could not load:', err));
    return () => { mounted = false; };
  }, [reloadKey]);

  const rowsById = useMemo(
    () => new Map((data ? buildBoardRows(data, verdictFor) : []).map((r) => [r.bill.id, r] as const)),
    [data, verdictFor],
  );
  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  return { rowsById, sendHistory: data?.sendHistory ?? null, reload };
}
